import React from 'react';
import { getSelectedCountTitle } from '../../../../utils/miscUtil';
import { Transaction } from '../../../../types/rest/assetCatalogTypes';
import _ from 'lodash';
import BlazeTable from '../../../../components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions, SelectAllType } from '../../../../components/blazeTable/BlazeTable.types';
import TransactionEditor from '../TransactionEditor/TransactionEditor';
import { getColoredHttpMethod } from '../../../../utils/commonUtil';
import SimpleTagList from '../../../../components/ui/tags/SimpleTagList';
import i18n from '../../../../i18n';
import { getDateWithFormat, getDaysRemainingPermanentDelete } from '../../../../utils/dateUtil';
import YellowExclamationIcon from '../../../../components/icons/YellowExclamationIcon';

interface RestoreTransactionTableProps {
  transactions: Transaction[];
  isLoading: boolean;
  setCurrentSelected: (ids: number[]) => void;
  selected: number[];
}

const TransactionsToRestoreTable = ({
  transactions,
  isLoading,
  setCurrentSelected,
  selected,
}: RestoreTransactionTableProps) => {
  const getExpandableComponent = (transaction: Transaction) => {
    return (
      <TransactionEditor
        transaction={transaction}
        isResponseBodyMagicStringInvalid={false}
        isResponseHeaderMagicStringInvalid={false}
        serviceId={transaction.serviceId}
        onSave={_.noop}
        isCreate={false}
        isReadOnly={true}
        isArchivedView={true}
      />
    );
  };

  const getColumnDefinition = (): BlazeTableColDef[] => [
    {
      accessor: 'id',
      cellComponent: (transaction: Transaction) => {
        return transaction.id;
      },
      header: 'ID',
      width: 100,
    },
    {
      accessor: 'name',
      isExpandLink: true,
      header: 'Name',
      width: 150,
    },
    {
      accessor: 'method',
      cellComponent: (transaction: Transaction) => {
        return getColoredHttpMethod(_.get(transaction, 'dsl.requestDsl.method') as any);
      },
      header: 'Method',
      isSortable: false,
      width: 100,
    },
    {
      accessor: 'serviceName',
      cellComponent: (transaction: Transaction) => {
        return <span>{transaction.serviceName}</span>;
      },
      header: 'Service',
      isSortable: false,
      width: 200,
    },
    {
      accessor: 'deletedDate',
      cellComponent: (transaction: Transaction) => {
        return <span>{getDateWithFormat(transaction.updated)}</span>;
      },
      isSortable: false,
      header: 'Deleted Date',
      width: 180,
    },
    {
      accessor: 'deletedBy',
      cellComponent: (transaction: Transaction) => {
        return <span>{transaction.updatedBy ? transaction.updatedBy : '-'}</span>;
      },
      isSortable: false,
      header: 'Deleted By',
      width: 180,
    },
    {
      accessor: 'DaysLeft',
      cellComponent: (transaction: Transaction) => {
        const daysRemaining: number = _.get(transaction, 'metadata.daysLeft');
        const limitBeforeWarning = 6;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ textAlignLast: 'justify' }}>{daysRemaining}</span>
            {daysRemaining < limitBeforeWarning && (
              <YellowExclamationIcon
                className={'ml-3'}
                size={18}
                title={i18n.t('transactions:table.daysRemainingTooltip')}
              />
            )}
          </div>
        );
      },
      isSortable: false,
      header: i18n.t('transactions:table.daysRemaining'),
      headerComponent: (
        <span title={i18n.t('transactions:table.daysRemainingTooltip')}>
          {i18n.t('transactions:table.daysRemaining')}
        </span>
      ),
      width: 180,
    },
    {
      accessor: 'tags',
      cellComponent: (transaction: Transaction) => {
        return (
          <div className={`qa-tbl-row-transaction-tags-${transaction.id} picked`}>
            {transaction.tags != null && <SimpleTagList tags={transaction.tags} />}
          </div>
        );
      },
      isSortable: false,
      header: 'Tags',
      width: 180,
    },
  ];

  const getTableOptions = (): BlazeTableOptions => {
    return {
      expandDetailComponent: (transaction: any) => getExpandableComponent(transaction),
      selectAllDropdown: true,
      showRowExpander: true,
      columnDef: getColumnDefinition(),
      pageable: true,
      selectable: true,
      pageSize: 10,
      rowPadding: 8,
      tableName: 'transactions',
      noResultsText: 'No results',
      minWidth: 650,
      sortable: true,
    };
  };

  return (
    <div>
      <h4>Transactions {getSelectedCountTitle(_.size(transactions), _.size(selected))}</h4>
      <p>{i18n.t('transactions:table.listTransactionsDescription')}</p>
      <BlazeTable
        onSelectionChange={(selectedItems, selectAll: SelectAllType) => {
          const ids = _.map(selectedItems, item => item.id);
          setCurrentSelected(ids);
        }}
        selectedItems={selected}
        data={transactions}
        options={getTableOptions()}
        isLoading={isLoading}
        showPageSizeDropdown={true}
        pageSizeOptions={[5, 10, 25]}
      />
    </div>
  );
};

export default TransactionsToRestoreTable;
